<template>
    <div class="program-component">
        <card title="Program Record Maintenance">
            <form>
                <SearchComponent v-model="program" :disabled="!disableFields" label="Program"
                    :paramsInGetById="{ includeDrugs: true, includePrescribers: true }" search-u-r-l="/Program"
                    prepopulate @input="onProgramUpdate" />
                <b-form-row>
                    <b-col>
                        <fg-input v-model="program.name" :disabled="disableFields" label="Name" required>
                        </fg-input>
                    </b-col>
                    <b-col>
                        <label>
                            Abbreviation
                            <help-popup placement="top">
                                Short identifier used throughout the system for this program (e.g., 'ACME', 'XYZ'). Displayed in dropdowns and reports.
                            </help-popup>
                        </label>
                        <fg-input v-model="program.abbreviation" :disabled="disableFields" required>
                        </fg-input>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col>
                        <label>
                            Memo
                            <help-popup placement="top">
                                Internal notes about this program. Not visible to patients or prescribers.
                            </help-popup>
                        </label>
                        <fg-input>
                            <textarea v-model="program.memo" :disabled="disableFields" class="form-control">
                            </textarea>
                        </fg-input>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col>
                        <checkbox v-model="program.active" :disabled=disableFields inline>
                            Active
                            <help-popup placement="top">
                                Controls whether this program is available for prescriptions, patients, etc. Inactive programs are hidden from selection lists.
                            </help-popup>
                        </checkbox>
                        <checkbox v-model="program.forceMemo" :disabled=disableFields inline>
                            Force Memo
                        </checkbox>
                        <checkbox v-model="program.autoprocess" :disabled=disableFields inline>
                            Autoprocess
                            <help-popup placement="top">
                                When enabled, eligible prescriptions automatically advance through processing stages without manual intervention. Requires Scriptly support for configuration.
                            </help-popup>
                        </checkbox>

                    </b-col>
                </b-form-row>

                <b-tabs pills content-class="mt-3" class="mt-3">
                    <b-tab title="Features" active>
                        <!-- Functionality Flags-->
                        <div class="feature-flags-grid">
                            <div class="feature-group">
                                <strong>Order Processing & Automation</strong>
                                <div>
                                    <div v-for="f in orderProcessingFlags" :key="f.id">
                                        <checkbox v-model="f.value" :disabled="disableFields" inline>
                                            {{ f.name }}
                                            <help-popup v-if="f.description" placement="top">{{ f.description }}</help-popup>
                                        </checkbox>
                                    </div>
                                </div>
                            </div>

                            <div class="feature-group">
                                <strong>Payment & Insurance</strong>
                                <div>
                                    <div v-for="f in paymentInsuranceFlags" :key="f.id">
                                        <checkbox v-model="f.value" :disabled="disableFields" inline>
                                            {{ f.name }}
                                            <help-popup v-if="f.description" placement="top">{{ f.description }}</help-popup>
                                        </checkbox>
                                    </div>
                                </div>
                            </div>

                            <div class="feature-group">
                                <strong>Inventory & Compliance</strong>
                                <div>
                                    <div v-for="f in inventoryComplianceFlags" :key="f.id">
                                        <checkbox v-model="f.value" :disabled="disableFields" inline>
                                            {{ f.name }}
                                            <help-popup v-if="f.description" placement="top">{{ f.description }}</help-popup>
                                        </checkbox>
                                    </div>
                                </div>
                            </div>

                            <div class="feature-group">
                                <strong>Patient Notifications & Messaging</strong>
                                <div>
                                    <div v-for="f in patientNotificationsFlags" :key="f.id">
                                        <checkbox v-model="f.value" :disabled="disableFields" inline>
                                            {{ f.name }}
                                            <help-popup v-if="f.description" placement="top">{{ f.description }}</help-popup>
                                        </checkbox>
                                    </div>
                                </div>
                            </div>

                            <div class="feature-group">
                                <strong>Other Features</strong>
                                <div>
                                    <div v-for="f in otherFeatureFlags" :key="f.id">
                                        <checkbox v-model="f.value" :disabled="disableFields" inline>
                                            {{ f.name }}
                                            <help-popup v-if="f.description" placement="top">{{ f.description }}</help-popup>
                                        </checkbox>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-tab>
                    <b-tab title="Operations">
                        <div class="operations-grid">
                            <div>
                                <div class="mb-2">
                                    <label>
                                        Delivery
                                        <help-popup placement="top">
                                            Default shipping method for this program (e.g., Mail, Local Delivery, Pickup).
                                            <br/><br/>
                                            <strong>Order Creation Priority:</strong> The actual delivery method used is determined by the highest priority available option:
                                            <ol style="margin: 5px 0; padding-left: 20px;">
                                                <li>Prescription-level delivery code (if set)</li>
                                                <li>Drug-specific delivery code in Clinical tab (if set)</li>
                                                <li>This program delivery method (if set)</li>
                                                <li>Patient's default delivery preference (if set)</li>
                                                <li>CII controlled substances default to Two Day shipping</li>
                                            </ol>
                                            <em>Higher priority methods (Will Call, Overnight, Two Day) override lower priority methods (Standard, Delivery).</em>
                                        </help-popup>
                                    </label>
                                    <EnumSelector v-model="program.deliveryMethod" :clearable="true"
                                        :disabled="disableFields" :enum-codes="deliveryCodes" />
                                </div>
                                <div>
                                    <label>
                                        Carriers
                                        <help-popup placement="top">
                                            Allowed shipping carriers for this program. Select all that apply (UPS, USPS, FedEx).
                                        </help-popup>
                                    </label>
                                    <multiselect v-model="program.carriers" group-label="label" group-values="carriers"
                                        :disabled="disableFields" :options="carrierOptions" :close-on-select="false"
                                        multiple group-select />
                                </div>
                            </div>
                            <div>
                                <div class="mb-3">
                                    <label>
                                        Assigned Teams
                                        <help-popup placement="top">
                                            Teams with access to process prescriptions for this program. If no teams are configured, all users can access the program.
                                        </help-popup>
                                    </label>
                                    <multiselect v-model="program.teams" track-by="id" label="name"
                                        :disabled="disableFields" :options="teamOptions" :close-on-select="false"
                                        multiple />
                                </div>
                                <div v-if="hasInventoryLocations" class="mb-2">
                                    <label>
                                        Default Inventory Filling Location
                                        <help-popup placement="top">
                                            Primary location used for filling prescriptions when multiple inventory locations exist.
                                        </help-popup>
                                    </label>
                                    <SearchComponent :id="program.defaultFillingLocation?.id"
                                        v-model="program.defaultFillingLocation" :disabled="disableFields"
                                        search-u-r-l="/inventory/inventory-locations" :prepopulate="true"
                                        clearable />
                                </div>
                                <div id="exclusive-transfer-row">
                                    <label>
                                        Exclusive Transfer Pharmacies
                                        <help-popup placement="top">
                                            When configured, prescriptions can only be transferred to/from these specific pharmacies. Leave empty to allow transfers to any pharmacy. <hr><br> Note: This also affects the available options for patients in the patient app.
                                        </help-popup>
                                    </label>
                                    <multiselect v-model="program.transferToPharmacies"
                                        :options="exclusiveTransferPharmacyOptions" :multiple="true" label="name"
                                        track-by="id" :loading="loadingExclusiveTransferPharmacyOptions"
                                        @search-change="exclusiveTransferPharmacySearch" :disabled="disableFields" />
                                </div>
                            </div>
                        </div>
                    </b-tab>
                    <b-tab title="Financial & Audits">
                        <b-form-row>
                            <b-col>
                                <label>
                                    Price Schedule
                                    <help-popup placement="top">
                                        Pricing structure applied to prescriptions in this program. Determines base pricing, markup, and dispensing fees when a price schedule is used as a cash out when billing a prescription.
                                    </help-popup>
                                </label>
                                <fg-input>
                                    <v-select v-model="selectedPriceSchedule" :disabled="disableFields"
                                        :options='priceSchedules' class="style-chooser" label='name' />
                                </fg-input>
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col>
                                <label>
                                    Audit Chance
                                    <HelpPopup markdown="Static number as a percentage value. e.g., 100 = 100% - Random chance within this threshold will create an order audit record that must be completed prior to dispensing in the Order Audit Queue." />
                                </label>
                                <fg-input id="audit-chance-input"
                                    v-model.number="orderAuditConfiguration.percentageChance" min=0 max=100
                                    :error="auditChanceError" :disabled="disableFields" type="number" />
                            </b-col>
                            <b-col>
                                <label>
                                    Minimum Margin
                                    <HelpPopup markdown="Dollar amount. e.g., 10 - Prescription margins below this threshold will create an order audit record that must be completed prior to dispensing in the Order Audit Queue." />
                                </label>
                                <fg-input id="audit-min-margin" v-model.number="orderAuditConfiguration.minimumMargin"
                                    :disabled="disableFields" type="number" />
                            </b-col>
                            <b-col>
                                <label>
                                    Maximum Margin
                                    <HelpPopup markdown="Dollar amount. e.g., 10 - Prescription margins above this threshold will create an order audit record that must be completed prior to dispensing in the Order Audit Queue." />
                                </label>
                                <fg-input id="audit-max-margin" v-model.number="orderAuditConfiguration.maximumMargin"
                                    :disabled="disableFields" type="number" />
                            </b-col>
                            <b-col>
                                <label>
                                    Maximum Copay
                                    <HelpPopup markdown="Dollar amount. e.g., 10 - A patient copay value above this threshold will create an order audit record that must be completed prior to dispensing in the Order Audit Queue." />
                                </label>
                                <fg-input id="audit-max-copay" v-model.number="orderAuditConfiguration.maximumCopay"
                                    :disabled="disableFields" type="number" />
                            </b-col>
                        </b-form-row>
                    </b-tab>
                    <b-tab title="Contacts">
                        <div class="contacts-grid">
                            <div>
                                <label><strong>Business Contact</strong>
                                    <HelpPopup placement="top">
                                        Primary business contact for this program. Informational purposes only.
                                    </HelpPopup>
                                </label>
                                <fg-input v-model="businessContact.firstName" :disabled="disableFields"
                                    label="First Name">
                                </fg-input>
                                <fg-input v-model="businessContact.lastName" :disabled="disableFields"
                                    label="Last Name">
                                </fg-input>
                                <fg-input v-model="businessContact.phone" :disabled="disableFields" label="Phone">
                                </fg-input>
                                <fg-input v-model="businessContact.emailAddress" :disabled="disableFields"
                                    label="Email Address">
                                </fg-input>
                            </div>
                            <div>
                                <label><strong>Program Contact</strong>
                                <HelpPopup placement="top">
                                        Primary contact for program-specific questions. Informational purposes only.
                                    </HelpPopup>
                                </label>
                                <fg-input v-model="programContact.firstName" :disabled="disableFields"
                                    label="First Name">
                                </fg-input>
                                <fg-input v-model="programContact.lastName" :disabled="disableFields" label="Last Name">
                                </fg-input>
                                <fg-input v-model="programContact.phone" :disabled="disableFields" label="Phone">
                                </fg-input>
                                <fg-input v-model="programContact.emailAddress" :disabled="disableFields"
                                    label="Email Address">
                                </fg-input>
                            </div>
                        </div>
                        <hr/>
                        <div class="contacts-grid">
                            <div class="mb-2">
                                <label>
                                    ApiKey
                                    <help-popup placement="top">
                                        Authentication key for external systems to integrate with this program via API. Keep this secure.
                                    </help-popup>
                                </label>
                                <fg-input v-model="program.apiKey" :disabled="disableFields"/>
                            </div>
                            <div></div>
                        </div>
                    </b-tab>
                    <b-tab title="Tasks">
                        <b-row cols="1">
                            <b-col class="mb-2">
                                <b-form-group>
                                    <label>
                                        Tasks SLA Business Days
                                        <help-popup placement="top">
                                            Service Level Agreement: number of business days before tasks for this program are considered overdue by default. Can be overriden on templates/ad-hoc tasks. Overdue tasks will send a notification to the assigned user/team.
                                        </help-popup>
                                    </label>
                                    <b-input v-model="program.userTaskSLADays" type="number" :disabled="disableFields"
                                        min="0"
                                        @blur="program.userTaskSLADays = !program.userTaskSLADays ? program.userTaskSLADays : Math.max(program.userTaskSLADays, 0)" />
                                </b-form-group>
                            </b-col>
                            <b-col class="mb-2">
                                <b-form-group>
                                    <label>
                                        Tasks Default Team
                                        <help-popup placement="top">
                                            Team automatically assigned to tasks created for this program if not overriden by a task template or via API.
                                        </help-popup>
                                    </label>
                                    <SearchComponent v-model="tasksDefaultTeam"
                                        search-u-r-l="/user/teams" :prepopulate="true" @input="taskTeamSelected"
                                        :disabled="disableFields" clearable />
                                </b-form-group>
                            </b-col>
                            <b-col class="mb-2">
                                <b-form-group>
                                    <label>
                                        Tasks Webhook Endpoint
                                        <help-popup placement="top">
                                            HTTP endpoint URL to receive task notifications via webhook when tasks are created, updated, or completed.
                                        </help-popup>
                                    </label>
                                    <b-input v-model="program.userTaskWebhookEndpoint" maxlength="150"
                                        :disabled="disableFields" />
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-tab>
                    <b-tab title="Clinical">
                        <b-row>
                            <!-- Prescribers Selection -->
                            <b-col>
                                <b-row>
                                    <b-col>
                                        <label>
                                            Doctors in Program
                                            <help-popup placement="top">
                                                Prescribers associated with this program. This association serves two purposes:
                                                <br/><br/>
                                                <strong>1. Automatic E-Script Routing:</strong> When an e-script arrives from a prescriber in this program, the prescription is automatically assigned to this program based on the prescriber's NPI.
                                                <br/><br/>
                                                <strong>2. Organizational Filtering:</strong> Helps filter and display prescribers related to the program in the user interface.
                                                <br/><br/>
                                                <em>Note: This does not restrict which prescribers can write prescriptions for patients in the program when created manually.</em>
                                            </help-popup>
                                        </label>
                                        <SearchComponent v-model="selectedDoctor" :disabled="disableFields"
                                            :search-u-r-l="`/Prescriber`" clearable
                                            @input="addDoctor" />
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col>
                                        <EntitySelectionList :list="prescribers" @remove-entity="removePrescriber" />
                                    </b-col>
                                </b-row>
                            </b-col>

                            <!-- Drugs Selection -->
                            <b-col>
                                <b-row>
                                    <b-col>
                                        <label>
                                            Drugs in Program
                                            <help-popup placement="top">
                                                Medications available in this program. Configure audits, delivery, and autoselect options for each drug.
                                            </help-popup>
                                        </label>
                                        <SearchComponent v-model="selectedDrug" :disabled="disableFields"
                                            search-u-r-l="/Drug" clearable @input="addDrug" />
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col>
                                        <EntitySelectionList :list="drugs" @remove-entity="removeDrug"
                                            :custom-id="getDrugId">
                                            <template v-slot:inputs='props'>
                                                <b-col cols="4">
                                                    <EnumSelector v-model="props.item.deliveryCode" label="Delivery"
                                                        :enum-codes="deliveryCodes" :disabled="disableFields"
                                                        :clearable="true">
                                                    </EnumSelector>
                                                </b-col>
                                                <b-col cols="2">
                                                    <b-form-group>
                                                        <label>
                                                            Autoselect
                                                            <help-popup placement="top">
                                                                When enabled, this drug is automatically selected when creating prescriptions for this program. Only one drug per program can have autoselect enabled.
                                                            </help-popup>
                                                        </label>
                                                        <b-checkbox v-if="!disableAutoselect(props.item)"
                                                            v-model="props.item.autoselect" size="lg"></b-checkbox>
                                                        <span v-else>
                                                            <b>Already set</b> on {{
                                                                getAutoselectProgramName(props.item) }}
                                                        </span>
                                                    </b-form-group>
                                                </b-col>
                                                <b-col cols="3">
                                                    <label>
                                                        Disable Autorefill
                                                        <help-popup placement="top">
                                                            Prevents automatic refill subscription creation for this drug, even if the program has autorefill enabled.
                                                        </help-popup>
                                                    </label>
                                                    <br>
                                                    <Checkbox v-model="props.item.disableAutorefill" />
                                                </b-col>
                                                <b-col cols='3'>
                                                    <b-form-group>
                                                        <label>
                                                            Awaits Prior Auth
                                                            <help-popup placement="top">
                                                                When enabled, e-commerce orders for prescriptions will fall into "Awaiting Prior Authorization" status for 5 days or until a PA is closed with an override.
                                                            </help-popup>
                                                        </label>
                                                        <b-checkbox v-model="props.item.awaitsPriorAuth" size="lg" />
                                                    </b-form-group>
                                                </b-col>
                                                <b-col cols="4">
                                                    <b-form-group>
                                                        <label>
                                                            Minimum Margin
                                                            <help-popup placement="top">
                                                                Drug-specific minimum profit margin override. Overrides program-level margin settings.
                                                            </help-popup>
                                                        </label>
                                                        <b-form-input v-model="props.item.minimumMargin" number
                                                            type="number" />
                                                    </b-form-group>
                                                </b-col>
                                                <b-col cols="4">
                                                    <b-form-group>
                                                        <label>
                                                            Maximum Margin
                                                            <help-popup placement="top">
                                                                Drug-specific maximum profit margin override. Overrides program-level margin settings.
                                                            </help-popup>
                                                        </label>
                                                        <b-form-input v-model="props.item.maximumMargin" number
                                                            type="number" />
                                                    </b-form-group>
                                                </b-col>
                                                <b-col cols="4">
                                                    <b-form-group>
                                                        <label>
                                                            Maximum Copay
                                                            <help-popup placement="top">
                                                                Drug-specific maximum copay override. Overrides program-level copay settings.
                                                            </help-popup>
                                                        </label>
                                                        <b-form-input v-model="props.item.maximumCopay" number
                                                            type="number" />
                                                    </b-form-group>
                                                </b-col>
                                            </template>
                                        </EntitySelectionList>
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>
                    </b-tab>
                    <b-tab title="Timing">
                        <div class="timing-grid">
                            <div>
                                <strong>
                                    Doctor Follow Up Next Touch Days
                                    <help-popup placement="top">
                                        Automated schedule for physician follow-up reminders. Configure number of days between follow-up attempts.
                                    </help-popup>
                                </strong>
                                <DoctorFollowUpNextTouchField :value="program.doctorFollowUpNextTouchDays"
                                    @input="program.doctorFollowUpNextTouchDays = $event" />
                            </div>
                            <div>
                                <strong>
                                    Prior Authorization Next Touch Days
                                    <help-popup placement="top">
                                        Automated schedule for prior authorization follow-up. Configure number of days between status check attempts.
                                    </help-popup>
                                </strong>
                                <PriorAuthNextTouchField :value="program.priorAuthNextTouchDays"
                                    @input="program.priorAuthNextTouchDays = $event" />
                            </div>
                            <div>
                                <strong>PAP Next Touch Days</strong>
                                <PAPNextTouchField :value="program.papNextTouchDays"
                                    @input="program.papNextTouchDays = $event" />
                            </div>
                        </div>

                        <div class="mt-3">
                            <strong>
                                Awaiting Insurance Text Hours
                                <HelpPopup># of Hours Between texts</HelpPopup>
                            </strong>

                            <b-input v-model="program.awaitingInsuranceHours" type="number" min="0"
                                :disabled="disableFields"
                                @blur="program.awaitingInsuranceHours = !program.awaitingInsuranceHours ? undefined : Math.round(Math.max(program.awaitingInsuranceHours, 0))" />
                        </div>
                    </b-tab>
                    <b-tab title="Text Messages">
                        <TextMessageTemplatesField v-model="program.textMessageTemplates" :disabled="disableFields"
                            :hide-templates="['passwordReset', 'awaitingInsurance', 'outOfNetwork', 'pharmacyInfo', 'transfer', 'rxRequestDenied']" />
                    </b-tab>
                    <b-tab title="Templates">
                        <!-- CUSTOM PATIENT EDUCATION TEMPLATE  -->
                        <label for=""><strong>Custom Patient Education Template</strong>
                            <HelpPopup popover-custom-class="text-message-template">
                                <div>
                                    <h6>Available Tokens <small class="text-muted">Click to copy to clipboard</small>
                                    </h6>
                                    <div class="tokens">
                                        <code v-for="tkn in availableTokens" :key="tkn"
                                            @click="copyText('\{\{' + tkn + '\}\}')">{{ "\{\{" +
                                                tkn + "\}\}" }}</code>
                                    </div>
                                </div>
                            </HelpPopup>
                        </label>
                        <div v-if="program && program.customTemplateFileId" class="mb-2">
                            A custom template was previously uploaded for this program. Click <a href="#"
                                @click.prevent="openCustomPatientEdTemplate">here</a> to download it.
                            <i>
                                Last updated:
                                {{
                                    program.customTemplateUpdated | formatDate('MM-DD-YYYY, h:mm:ss a')
                                }}
                            </i>
                        </div>

                        <b-form-file v-model="customTemplateFile" :disabled="disableFields" accept=".docx"
                            drop-placeholder="Drop file here..."
                            placeholder="Choose your custom template or drop it here...">
                        </b-form-file>

                        <hr>

                        <label>
                            <strong>Email Templates</strong>
                            <help-popup placement="top">
                                Customize email messages sent to patients for this program. Override global templates with program-specific messaging.
                            </help-popup>
                        </label>
                        <EmailTemplatesField v-model="program.emailTemplates" :disabled="disableFields" />

                        <hr />

                        <div v-if="!!program.id" class="fax-templates-section">
                            <FaxTemplateField v-model="program.faxTemplateOverrides" />
                        </div>
                    </b-tab>
                </b-tabs>
            </form>

            <NewClearSaveButtons :disable="disableFields" :disable-clear="disableFields" :disable-save="!isFormValid"
                :titles="buttonTitles" @clear-clicked="clearForm" @new-clicked="createProgram"
                @save-clicked="saveProgram" sticky />

        </card>
    </div>
</template>

<script lang="ts">
import axios, { CancelTokenSource } from 'axios';
import { Component, Mixins, Watch } from 'vue-property-decorator';
import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';

import TextMessageTemplatesField from "@/components/Configuration/TextMessageTemplatesField.vue";
import SearchComponent from '@/components/SearchComponent.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import EntitySelectionList from "@/components/EntitySelectionList.vue";
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';
import EnumSelector from '@/components/EnumSelector.vue';
import Multiselect from "vue-multiselect";
import { Program } from '@/models/Program';
import { Prescriber } from '@/models/Prescriber';
import { Drug, DrugProgram } from '@/models/Drug/Drug';
import { Contact, ContactType } from "@/models/Contact";
import { FunctionalityFlag } from "@/models/FunctionalityFlag";
import { PriceSchedule } from "@/models/PriceSchedule";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { DeliveryCode } from '@/models/DeliveryCode';
import { OrderAuditConfiguration } from "@/models/OrderAudit";
import { DrugProduct, DrugSource } from '@/models/Drug/DrugProduct';
import { EntityType } from "@/models/EntityType";
import { PharmacyRecord } from "@/models/Pharmacy";
import { Team } from '@/models/Team';
import { debounce } from 'throttle-debounce';
import Axios from 'axios';
import DoctorFollowUpNextTouchField from '@/components/Configuration/DoctorFollowUpNextTouchField.vue';
import PriorAuthNextTouchField from '@/components/Configuration/PriorAuthNextTouchField.vue';
import PAPNextTouchField from '@/components/Configuration/PAPNextTouchField.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import EmailTemplatesField from "@/components/Configuration/EmailTemplatesField.vue";
import { ButtonTitles } from "@/components/ButtonTitles";
import FaxTemplateField from "@/components/Configuration/FaxTemplateField.vue";

@Component({
    name: 'ProgramDetailsPage',
    components: {
        EmailTemplatesField,
        SearchComponent,
        Checkbox,
        NewClearSaveButtons,
        EntitySelectionList,
        EnumSelector,
        Multiselect,
        TextMessageTemplatesField,
        DoctorFollowUpNextTouchField,
        PriorAuthNextTouchField,
        PAPNextTouchField,
        HelpPopup,
        FaxTemplateField
    },
})
export default class ProgramDetailsPage extends Mixins(DisableAutocompleteMixin) {
    private program: Program = new Program();
    private businessContact: Contact = { type: ContactType.Business } as Contact;
    private programContact: Contact = { type: ContactType.Program } as Contact;
    private flags: FunctionalityFlag[] = [];
    private clearFlags: FunctionalityFlag[] = [];
    private priceSchedules: PriceSchedule[] = [];
    private selectedPriceSchedule: PriceSchedule = {} as PriceSchedule;
    private customTemplateFile: File = new File([], "");
    protected deliveryCodes = DeliveryCode;

    protected selectedDoctor: Prescriber = new Prescriber();
    private prescribers: Array<Prescriber> = new Array<Prescriber>();

    protected selectedDrug: DrugProduct = new Drug();
    private drugs: Array<DrugProgram> = new Array<DrugProgram>();
    private orderAuditConfiguration: OrderAuditConfiguration = new OrderAuditConfiguration();
    protected pharmacy: PharmacyRecord = new PharmacyRecord();

    protected tasksDefaultTeam: Team = new Team();

    protected buttonTitles: ButtonTitles = {
        save: "Save Changes",
        new: "New Program",
        clear: "Clear",
        cancel: "",
    };

    protected availableTokens: string[] = ["PatientName", "PatientAddress", "DateOfBirth", "PatientGender", "CP",
        "PrescriberName", "Shipping", "FillDate", "DrugName", "Qty", "RxNbr",
        "RfNbr", "Rf", "TotalPrice", "Copay", "RfLeft", "ProgramName",
        "InventoryLocation", "NDC", "DescriptionHeader", "Description",
        "DescriptionFooter", "ContraindicationsHeader", "Contraindications",
        "ContraindicationsFooter", "AdministrationHeader", "Administration",
        "AdministrationFooter", "MissedDoseHeader", "MissedDose", "MissedDoseFooter",
        "MonitoringHeader", "Monitoring", "MonitoringFooter", "SideEffectsHeader",
        "SideEffects", "SideEffectsFooter", "StorageHeader", "Storage",
        "StorageFooter", "ConsignmentRx", "PharmacyInfo", "PharmacyPhone"];

    protected carrierOptions = [{ label: "All Carriers", carriers: ['UPS', 'USPS', 'FedEx'] }];
    protected teamOptions: Team[] = [];

    protected exclusiveTransferPharmacyOptions: Array<PharmacyRecord> = [];
    protected loadingExclusiveTransferPharmacyOptions = false;

    private exclusiveTransferPharmacySearchCancelTokenSource: CancelTokenSource | null = null;

    exclusiveTransferPharmacySearch(searchQuery: string) {
        this.exclusiveTransferPharmacySearchCancelTokenSource?.cancel("New search started");
        this.doDebouncedExclusiveTransferPharmacySearch(searchQuery, this);
    }

    private doDebouncedExclusiveTransferPharmacySearch = debounce(500, async (searchQuery, context) => {
        if (!searchQuery?.trim()) {
            context.exclusiveTransferPharmacyOptions = [];
            return;
        }

        context.loadingExclusiveTransferPharmacyOptions = true;

        context.exclusiveTransferPharmacySearchCancelTokenSource = Axios.CancelToken.source();

        try {
            const resp = await Axios.get<PharmacyRecord[]>("/StoredPharmacy", {
                params: { searchTerm: searchQuery, showOnlyActive: true },
                cancelToken: context.exclusiveTransferPharmacySearchCancelTokenSource.token
            });
            console.log('tokens: ', resp.data);

            context.exclusiveTransferPharmacyOptions = resp.data;
        } catch (err) {
            console.log(err);
        } finally {
            context.loadingExclusiveTransferPharmacyOptions = false;
        }
    })

    private newMode = false;

    get routeProgramId(): number {
        let res = parseInt(this.$route.params.id);
        if (isNaN(res)) return 0;
        return res;
    }

    get disableFields(): boolean {
        return !(this.program && this.program.id) && !this.newMode;
    }

    get serverURL() {
        return axios.defaults.baseURL;
    }

    get isFormValid(): boolean {
        return !!(this.program?.name && this.program?.abbreviation);
    }

    get auditChanceError(): string {
        if (this.orderAuditConfiguration.percentageChance == undefined) return "";
        if (this.orderAuditConfiguration.percentageChance > 100) return "Must be 100 or less";
        if (this.orderAuditConfiguration.percentageChance < 0) return "Must be 0 or greater";
        return "";
    }

    get filteredFeatureFlags() {
        return this.flags.filter(flag => {
            const name = flag.name.toLowerCase();
            return name !== 'commissionable';
        });
    }

    get orderProcessingFlags() {
        return this.filteredFeatureFlags.filter(flag => {
            const name = flag.name.toLowerCase();
            return name.includes('autoprocess') ||
                name.includes('autoship') ||
                name.includes('autorefill') ||
                name.includes('pv1') ||
                name.includes('autoselect only') ||
                name.includes('refills') ||
                name.includes('transfer');
        });
    }

    get paymentInsuranceFlags() {
        return this.filteredFeatureFlags.filter(flag => {
            const name = flag.name.toLowerCase();
            if (this.orderProcessingFlags.some(f => f.id === flag.id)) return false;
            return name.includes('collected by partner') ||
                name.includes('copay') ||
                name.includes('tracking text') ||
                name.includes('insurance') ||
                name.includes('pending billing') ||
                name.includes('skip prior auth');
        });
    }

    get inventoryComplianceFlags() {
        return this.filteredFeatureFlags.filter(flag => {
            const name = flag.name.toLowerCase();
            if (this.orderProcessingFlags.some(f => f.id === flag.id)) return false;
            if (this.paymentInsuranceFlags.some(f => f.id === flag.id)) return false;
            return name.includes('inventory') ||
                name.includes('lot number') ||
                name.includes('serial number') ||
                name.includes('print queue') ||
                name.includes('smart post') ||
                name.includes('smartpost');
        });
    }

    get patientNotificationsFlags() {
        return this.filteredFeatureFlags.filter(flag => {
            const name = flag.name.toLowerCase();
            if (this.orderProcessingFlags.some(f => f.id === flag.id)) return false;
            if (this.paymentInsuranceFlags.some(f => f.id === flag.id)) return false;
            if (this.inventoryComplianceFlags.some(f => f.id === flag.id)) return false;
            return name.includes('notification') ||
                name.includes('text') ||
                name.includes('refill request') ||
                name.includes('patientapp') ||
                name.includes('contact') ||
                name.includes('delivery');
        });
    }

    get otherFeatureFlags() {
        return this.filteredFeatureFlags.filter(flag => {
            return !this.orderProcessingFlags.some(f => f.id === flag.id) &&
                !this.paymentInsuranceFlags.some(f => f.id === flag.id) &&
                !this.inventoryComplianceFlags.some(f => f.id === flag.id) &&
                !this.patientNotificationsFlags.some(f => f.id === flag.id);
        });
    }

    async created() {
        console.log(`%c Created ${this.$options.name}`, "color: green");

        const functionalitiesPromise = axios.get(`/Program/${0}/functionalities`);
        const priceSchedulesPromise = axios.get(`/PriceSchedule`);
        const teamsPromise = axios.get<Team[]>('/api/User/teams');

        try {
            const functionalitiesResponse = await functionalitiesPromise;
            this.clearFlags = functionalitiesResponse.data;
            this.flags = functionalitiesResponse.data;
        } catch (error) {
            console.warn(error);
        }

        try {
            const priceSchedulesResponse = await priceSchedulesPromise;
            this.priceSchedules = priceSchedulesResponse.data;
        } catch (error) {
            console.warn(error);
        }

        try {
            const teamsResponse = await teamsPromise;
            this.teamOptions = teamsResponse.data.map(t => new Team(t));
        } catch (error) {
            console.warn(error);
        }

        if (this.routeProgramId) {
            this.fetchProgram(this.routeProgramId);
        }

        this.loadHasInventoryLocations();
    }

    taskTeamSelected() {
        this.program.userTaskDefaultTeamId = this.tasksDefaultTeam?.id;
    }

    createProgram() {
        this.program = new Program();
        this.program.active = true;
        this.newMode = true;
        this.flags = this.clearFlags;
    }

    async clearForm() {
        try {
            const value = await this.$bvModal.msgBoxConfirm('Are you sure you want to clear?', {
                title: 'Confirm',
                okVariant: 'danger',
                centered: true,
            });

            if (!value) return;

            this.program = new Program();
            this.newMode = false;
            this.businessContact = { type: ContactType.Business } as Contact;
            this.programContact = { type: ContactType.Program } as Contact;
            this.selectedPriceSchedule = {} as PriceSchedule;
            this.flags = this.clearFlags;
            this.selectedDoctor = new Prescriber();
            this.selectedDrug = new Drug();
            this.drugs = new Array<DrugProgram>();
            this.prescribers = new Array<Prescriber>();
            this.orderAuditConfiguration = new OrderAuditConfiguration();
            this.pharmacy = new PharmacyRecord();
            this.tasksDefaultTeam = new Team();
        } catch (err) {
            console.log("Error caught in clearForm()");
            console.error(err);
        }
    }

    getAutoselectProgramName(item: DrugProgram) {
        return item.autoselectProgram && item.autoselectProgram.abbreviation;
    }

    async saveProgram() {
        if (!this.program) {
            console.warn("We have no program to save, get a new one?");
            return;
        }

        const data = this.program;
        data.programContact = this.programContact;
        data.businessContact = this.businessContact;
        data.priceSchedule = this?.selectedPriceSchedule?.name ? this.selectedPriceSchedule : null;
        data.priceScheduleId = this.selectedPriceSchedule?.name ? this.selectedPriceSchedule.id : null;
        data.flags = this.flags;
        data.prescribers = this.prescribers;
        data.drugs = this.drugs;
            if (this.program.priceAdjustment?.toString() == "") {
                data.priceAdjustment = null;
            }
        const orderAuditConfigurations = this.program.orderAuditConfigurations;

        try {
            const response = await axios.post<Program>('api/program', data);
            this.program = new Program(response.data);

            // Upload custom template if there is any
            if (this.customTemplateFile.size > 0) {
                try {
                    const formData = new FormData();
                    formData.append('customTemplate', this.customTemplateFile);
                    const templateResponse = await axios.post<Program>(
                        `api/program/custom-template/${this.program.id}`,
                        formData,
                        { headers: { 'Content-Type': 'multipart/form-data' } }
                    );
                    this.program = new Program(templateResponse.data);
                    if (this.program.customTemplateFileId > 0) {
                        this.$notification(NotificationOptions.successSaveNotificationPreset('Custom Template'));
                        console.log(templateResponse);
                    }
                } catch (err) {
                    this.$notification(NotificationOptions.errorSaveNotificationPreset('Custom Template', err));
                    console.error(err, { err, response: err?.response });
                }
            }

            this.orderAuditConfiguration.entityId = +this.program.id;
            this.orderAuditConfiguration.entityType = EntityType.Program;
            this.orderAuditConfiguration.sanitize();

            let orderAuditConfigs = [];
            for (const drug of this.drugs) {
                let orderAuditConfig = orderAuditConfigurations?.find(v => v.id == drug.orderAuditId);
                if (!orderAuditConfig) {
                    orderAuditConfig = new OrderAuditConfiguration();
                } else {
                    orderAuditConfig = new OrderAuditConfiguration(orderAuditConfig);
                }
                orderAuditConfig.entityId = +this.program.id;
                orderAuditConfig.entityType = EntityType.Program;
                orderAuditConfig.minimumMargin = drug.minimumMargin;
                orderAuditConfig.maximumMargin = drug.maximumMargin;
                orderAuditConfig.maximumCopay = drug.maximumCopay;
                orderAuditConfig.drugId = drug.packageID;
                orderAuditConfig.drugSource = DrugSource.GsddFederal;
                orderAuditConfig.sanitize();

                orderAuditConfigs.push(orderAuditConfig);
            }
            orderAuditConfigs.push(this.orderAuditConfiguration);

            try {
                const resp = await axios.post(`/api/Program/orderAuditConfigurations`, orderAuditConfigs);
                console.log("resp: ", resp);
            } catch (err) {
                this.$notification(NotificationOptions.error(err));
            } finally {
                this.onProgramUpdate(this.program);
            }

            this.$notification(NotificationOptions.notificationOptionsPreset(
                "Program Successfully Saved",
                NotificationOptions.NotificationTypes.success
            ));
        } catch (error) {
            console.error("Error on saving program", error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    @Watch('$route.params.id')
    onParamChanged(value: string, oldValue: string) {
        if (value != oldValue) {
            this.fetchProgram(Number(value));
        }
    }

    @Watch('program')
    onProgramChanged(value: Program | null, _oldValue: Program | null) {
        if (!value) return;

        if (value.id && value.id != 0 && value.id != this.routeProgramId) {
            this.$router.push({ name: 'ProgramDetails', params: { id: value.id.toString() } });
        }

        if (this.routeProgramId && value.id == 0) {
            this.$router.push({ name: 'Program' });
        }
    }

    async fetchProgram(programId: number) {
        if (!programId) {
            this.program = new Program();
            return;
        }

        const contactsPromise = axios.get<Array<Contact>>(`/Program/${programId}/contacts`);
        const programPromise = axios.get<Program>(`api/Program/${programId}`, {
            params: {
                includeDrugs: true,
                includePrescribers: true,
                includeFlags: true,
                includeAuditConfigurations: true,
            },
        });

        // Get Contacts
        try {
            const contactsResponse = await contactsPromise;
            const contacts = contactsResponse.data;
            this.programContact = contacts.filter(con => con.type == ContactType.Program)[0] || ({ type: ContactType.Program } as Contact);
            this.businessContact = contacts.filter(con => con.type == ContactType.Business)[0] || ({ type: ContactType.Business } as Contact);
            this.customTemplateFile = new File([], "");
            this.selectedDoctor = new Prescriber();
            this.selectedDrug = new Drug();
        } catch (error) {
            console.warn(error);
        }

        try {
            const response = await programPromise;

            if (response.data?.id) {
                const program: Program = new Program(response.data);
                this.program = program;
                this.drugs = new Array<DrugProgram>();
                if (program?.drugs?.length) {
                    this.drugs = program.drugs?.map(dp => new DrugProgram(dp));
                }

                this.prescribers = program.prescribers?.map(doc => Object.assign(new Prescriber(), doc)) || ([] as Array<Prescriber>);
                this.flags = program.flags;
                this.selectedPriceSchedule = this.priceSchedules.filter(sm => sm.id == this.program?.priceSchedule?.id)[0];
                if (this.program.orderAuditConfigurations?.length) {
                    const auditConfigurations = this.program.orderAuditConfigurations;
                    this.orderAuditConfiguration = new OrderAuditConfiguration(auditConfigurations.find(v => !v.drugId));
                    for (let drug of this.drugs) {
                        let drugAuditConfig = auditConfigurations.find(v => v.drugId == drug.packageID);
                        if (drugAuditConfig) {
                            drug.orderAuditId = drugAuditConfig.id;
                            drug.minimumMargin = drugAuditConfig.minimumMargin;
                            drug.maximumMargin = drugAuditConfig.maximumMargin;
                            drug.maximumCopay = drugAuditConfig.maximumCopay;
                        } else {
                            console.warn("no config found for drug: ", drug);
                        }
                    }
                }

                if (this.program.userTaskDefaultTeamId) {
                    try {
                        const teamResponse = await axios.get<Team>(`/api/user/teams/${this.program.userTaskDefaultTeamId}`);
                        this.tasksDefaultTeam = new Team(teamResponse.data);
                    } catch (error) {
                        console.warn(error);
                    }
                }
            }
        } catch (error) {
            console.error('Error while getting program data.', { error, response: error?.response });
        }
    }

    onProgramUpdate(value: Program) {
        return this.fetchProgram(value.id);
    }

    addDoctor(val: Prescriber) {
        if (val && val.id) {
            const wasAdded = this.prescribers.find(doc => doc.id == val.id);
            if (!wasAdded)
                this.prescribers.push(val);
        }
    }

    removePrescriber(prescriber: Prescriber) {
        this.prescribers = this.prescribers.filter(doc => doc.id != prescriber.id);
    }

    async addDrug(val: DrugProduct) {
        if (!val || !val.drugId) return;

        const wasAdded = this.drugs.filter(doc => doc.packageID == Number(val.drugId))[0];
        if (wasAdded) return;

        const dp = new DrugProgram();
        dp.drug = val;

        const url = `api/drug/${val.drugId}/${val.source}/Program`;

        try {
            //Find if selected drug has already been set for autoselect for another program
            const res = await axios.get<Array<DrugProgram>>(url);
            if (res.data?.length) {
                //Get programs for drug and find the one with autoselect
                const dps = res.data.map(dp => new DrugProgram(dp));
                const autoselectProgram = dps.find(dp => dp.autoselect)?.program || null;
                dp.autoselect = false;
                dp.autoselectProgram = autoselectProgram;
            }
        } catch (err) {
            console.error('Error getting Program information for selected drug', {
                err,
                response: err?.response,
            });
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.drugs.push(dp);
        }
    }

    disableAutoselect(dp: DrugProgram): boolean {
        return !!(dp.autoselectProgram?.id && dp.autoselectProgram?.id != this.program.id);
    }

    removeDrug(drug: DrugProgram) {
        let idx = this.drugs.findIndex(dg => dg.packageID == drug.packageID && dg.drugSource == drug.drugSource);
        if (idx != -1) {
            this.drugs.splice(idx, 1);
        }
    }

    getDrugId(drug: DrugProgram) {
        return `${drug.drugSource}-${drug.packageID}`;
    }

    protected hasInventoryLocations = false;

    async loadHasInventoryLocations() {
        try {
            this.hasInventoryLocations = (await axios.get<boolean>("/inventory/has-inventory-locations")).data;
        } catch {
            //ignore
        }
    }

    async openCustomPatientEdTemplate() {
        try {
            const response = await axios.get(`program/custom-template/${this.program.id}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(response.data);
            window.open(objectUrl, '_blank');
        } catch (err) {
            console.error("Error while getting custom template.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    copyText(text: string) {
        navigator.clipboard.writeText(text);
        this.$notification(NotificationOptions.success(`Copied token ${text} to clipboard.`));
    }

} // close export of class

</script>


<style scoped>
/*noinspection CssUnusedSymbol*/
.form-check {
    margin-top: 0;
    padding-left: 0;
}

.functionality-flags {
    column-count: 4;
    gap: 8px;
}

.functionality-flags>div {
    margin-bottom: 8px;
}

.feature-flags-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 16px;
}

.feature-group {
    display: flex;
    flex-direction: column;
}

.feature-group strong {
    margin-bottom: 8px;
}

.feature-group>div>div {
    margin-bottom: 8px;
}

.timing-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(525px, 1fr));
    gap: 16px;
}

.contacts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 24px;
}

.contacts-grid>div {
    display: flex;
    flex-direction: column;
}

.contacts-grid>div>label {
    margin-bottom: 12px;
}

.operations-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 24px;
}
</style>
